import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { KeyRound, ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Missing reset token — please use the link from your email");
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated — please sign in");
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 grid-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-md bg-zinc-900 flex items-center justify-center">
            <KeyRound className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-zinc-900">
              Reset Password
            </h1>
            <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
              Shipment Code Generator
            </p>
          </div>
        </div>

        <Card className="border-zinc-200 shadow-sm">
          {done ? (
            <div className="p-7 text-center flex flex-col items-center gap-3">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              <h2 className="text-lg font-semibold text-zinc-900">Password updated</h2>
              <p className="text-sm text-zinc-500">Redirecting you to sign-in…</p>
            </div>
          ) : !token ? (
            <div className="p-7 text-center flex flex-col items-center gap-3">
              <XCircle className="w-10 h-10 text-red-600" />
              <h2 className="text-lg font-semibold text-zinc-900">Missing token</h2>
              <p className="text-sm text-zinc-500">
                Open the link from your reset email — the token is included as a URL parameter.
              </p>
              <Link to="/login" className="text-sm text-blue-700 hover:underline mt-2">
                Back to sign-in →
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-7 flex flex-col gap-4">
              <div>
                <h2 className="text-lg font-semibold text-zinc-900">Choose a new password</h2>
                <p className="text-sm text-zinc-500 mt-1">
                  Minimum 6 characters. Don&apos;t reuse an old one.
                </p>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pw" className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
                  New password
                </Label>
                <Input
                  id="pw"
                  type="password"
                  data-testid="reset-pw"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  autoComplete="new-password"
                  className="h-11"
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pw2" className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
                  Confirm password
                </Label>
                <Input
                  id="pw2"
                  type="password"
                  data-testid="reset-pw2"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  autoComplete="new-password"
                  className="h-11"
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                data-testid="reset-submit"
                className="h-11 bg-zinc-900 hover:bg-zinc-800 text-white mt-2"
              >
                {submitting ? "Updating…" : "Update password"}
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>

              <Link to="/login" className="text-xs text-blue-700 hover:underline text-center mt-1">
                Back to sign-in
              </Link>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
